import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAgentRecords } from '../services/agentRecordsService';

const CreateAgentModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { currentAgent } = useAuth();
  
  const [formData, setFormData] = useState({
    agentId: '',
    phoneNo: '',
    email: '',
    name: '',
    role: 'booking',
    teamLeader: '', // Default to no team leader
    category: 'POP' // Default category
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showSuccessState, setShowSuccessState] = useState(false);
  const [agentIdCheck, setAgentIdCheck] = useState({ checking: false, available: null });
  const [teamLeaders, setTeamLeaders] = useState([]); // List of available team leaders
  const [loadingTeamLeaders, setLoadingTeamLeaders] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Set category to match creator's category, default to POP if not set
      const creatorCategory = currentAgent?.category || 'POP';
      
      setFormData({
        agentId: '',
        phoneNo: '',
        email: '',
        name: '',
        role: 'booking',
        teamLeader: '', // Default to no team leader
        category: creatorCategory // Match creator's category
      });
      setError('');
      setSuccess('');
      setAgentIdCheck({ checking: false, available: null });
      setShowSuccessState(false);
      
      // Fetch team leaders if user is admin
      if (currentAgent?.role === 'admin') {
        fetchTeamLeaders();
      }
    }
  }, [isOpen, currentAgent]);

  // Fetch all agents to use as team leaders (for admin only)
  const fetchTeamLeaders = async () => {
    setLoadingTeamLeaders(true);
    try {
      const agents = await getAgentRecords();
      
      // Filter agents with roles: teamLeader, admin, or user
      const filteredAgents = agents.filter(agent => {
        const role = agent.apps?.agents?.role;
        return role === 'teamLeader' || role === 'admin' || role === 'user';
      });
      
      // Format and sort by name
      const formattedAgents = filteredAgents.map(agent => ({
        agent_id: agent.agent_id,
        name: agent.name,
        role: agent.apps?.agents?.role
      }));
      
      formattedAgents.sort((a, b) => a.name.localeCompare(b.name));
      setTeamLeaders(formattedAgents);
    } catch (err) {
      console.error('Error fetching team leaders:', err);
      setError('Failed to load team leaders list');
    } finally {
      setLoadingTeamLeaders(false);
    }
  };

  // Define role options based on current user's role
  const getRoleOptions = () => {
    // Users can only create Booking Partner agents
    if (currentAgent?.role === 'user') {
      return [
        { value: 'booking', label: 'Booking Partner' }
      ];
    }

    // Team leaders can create user and lower roles only (not teamLeader or admin)
    if (currentAgent?.role === 'teamLeader') {
      return [
        { value: 'booking', label: 'Booking Partner' },
        { value: 'user', label: 'User' }
      ];
    }

    // Add admin and user roles if current user is admin
    if (currentAgent?.role === 'admin') {
      const baseRoles = [
        { value: 'booking', label: 'Booking Partner' }
      ];
      return [
        ...baseRoles,
        { value: 'user', label: 'User' },
        { value: 'teamLeader', label: 'Team Leader' },
        { value: 'admin', label: 'Admin' }
      ];
    }

    // Other roles cannot create agents
    return [];
  };

  const roleOptions = getRoleOptions();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'agentId') {
      // Convert to uppercase and limit to 3 characters
      setFormData(prev => ({
        ...prev,
        [name]: value.toUpperCase().slice(0, 3)
      }));
      
      // Check availability if we have 3 characters
      const upperValue = value.toUpperCase().slice(0, 3);
      if (upperValue.length === 3) {
        checkAgentIdAvailability(upperValue);
      } else {
        setAgentIdCheck({ checking: false, available: null });
      }
    } else if (name === 'phoneNo') {
      // Allow + and digits for international format
      setFormData(prev => ({
        ...prev,
        [name]: value.replace(/[^\d+]/g, '')
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const checkAgentIdAvailability = async (agentId) => {
    setAgentIdCheck({ checking: true, available: null });
    
    try {
      const response = await fetch('https://europe-west3-plsss-project.cloudfunctions.net/checkAgentIdAvailability', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ agentId })
      });
      
      const result = await response.json();
      
      setAgentIdCheck({
        checking: false,
        available: result.available
      });
    } catch (err) {
      console.error('Error checking agent ID:', err);
      setAgentIdCheck({ checking: false, available: null });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate current agent exists
    if (!currentAgent || !currentAgent.agent_id) {
      setError('Current agent information not found. Please refresh and try again.');
      setLoading(false);
      return;
    }

    // Validate form
    if (!formData.agentId || !formData.phoneNo || !formData.email || !formData.name) {
      setError('All fields are required');
      setLoading(false);
      return;
    }

    // Validate agent ID availability
    if (agentIdCheck.available === false) {
      setError('Agent ID is not available. Please choose a different ID.');
      setLoading(false);
      return;
    }

    try {
      // Create agent document via HTTP request
      const documentId = `${formData.agentId.toUpperCase()}${formData.phoneNo.replace('+', '')}`;
      const agentData = {
        agentId: formData.agentId.toUpperCase(),
        phoneNo: formData.phoneNo,
        email: formData.email.toLowerCase().trim(),
        name: formData.name.trim(),
        role: formData.role, // Include selected role
        createdBy: currentAgent.agent_id // Track who created this agent
      };

      // Only include teamLeader if it's not empty
      if (formData.teamLeader && formData.teamLeader.trim() !== '') {
        agentData.teamLeader = formData.teamLeader;
      }

      // Include category
      agentData.category = formData.category;

      const response = await fetch('https://europe-west3-plsss-project.cloudfunctions.net/createAgent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(agentData)
      });
      
      const result = await response.json();
      
      if (result.success) {
        const whatsappInfo = result.whatsappMessages ? 
          `\n\nWhatsApp notification sent:\n${result.whatsappMessages.map(msg => 
            `• Team Leader: ${msg.phone}`
          ).join('\n')}\n\nNew agent will receive welcome message via separate process.` : '';
        
        setSuccess(`Agent created successfully! Document ID: ${result.documentId}${whatsappInfo}`);
        setShowSuccessState(true);
        
        // Don't close modal immediately - let user see success and dismiss
      } else {
        setError(result.error || 'Failed to create agent');
      }
      
    } catch (err) {
      console.error('Error creating agent:', err);
      setError(err.message || 'Failed to create agent. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onClose();
    navigate('/dashboard');
  };

  const handleDismissSuccess = () => {
    onClose();
    navigate('/dashboard');
  };

  const handleClose = () => {
    if (!loading) {
      handleCancel();
    }
  };

  if (!isOpen) return null;

  // Check if current user is admin
  const isAdmin = currentAgent?.role === 'admin';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Create New Agent</h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}

          {success ? (
            <div className="text-center py-8">
              <div className="mb-4">
                <svg className="w-16 h-16 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Agent Created Successfully!</h3>
              <p className="text-gray-600 mb-4 whitespace-pre-line">{success}</p>
              <p className="text-sm text-gray-500 mb-6">WhatsApp notifications have been sent to the new agent and team leader with registration details.</p>
              
              <div className="flex justify-center space-x-3">
                <button
                  onClick={handleDismissSuccess}
                  className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Dismiss
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Agent ID Field */}
              <div>
                <label htmlFor="agentId" className="block text-sm font-medium text-gray-700 mb-1">
                  Agent ID (3 letters)
                </label>
                <div className="relative">
                  <input
                    type="text"
                    id="agentId"
                    name="agentId"
                    value={formData.agentId}
                    onChange={handleInputChange}
                    placeholder="e.g., XYZ"
                    maxLength={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase"
                    required
                  />
                  {agentIdCheck.checking && (
                    <div className="absolute right-3 top-2.5">
                      <svg className="w-5 h-5 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}
                  {agentIdCheck.available === true && (
                    <div className="absolute right-3 top-2.5">
                      <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                  {agentIdCheck.available === false && (
                    <div className="absolute right-3 top-2.5">
                      <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </div>
                  )}
                </div>
                {agentIdCheck.available === false && (
                  <p className="text-red-500 text-xs mt-1">This Agent ID is already taken</p>
                )}
                {agentIdCheck.available === true && (
                  <p className="text-green-500 text-xs mt-1">This Agent ID is available</p>
                )}
              </div>

              {/* Phone Number Field */}
              <div>
                <label htmlFor="phoneNo" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number (include country code)
                </label>
                <input
                  type="tel"
                  id="phoneNo"
                  name="phoneNo"
                  value={formData.phoneNo}
                  onChange={handleInputChange}
                  placeholder="+97333123456"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="e.g., agent@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Name Field */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., John Doe"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              {/* Role Field */}
              <div>
                <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1">
                  Agent Role
                </label>
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  {roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Select the role for this agent
                  {isAdmin && ' (Admin users can assign all roles)'}
                </p>
              </div>

              {/* Team Leader Field - Conditionally rendered */}
              <div>
                <label htmlFor="teamLeader" className="block text-sm font-medium text-gray-700 mb-1">
                  Team Leader
                </label>
                {isAdmin ? (
                  // Admin users see a dropdown to select or remove team leader
                  <div>
                    <select
                      id="teamLeader"
                      name="teamLeader"
                      value={formData.teamLeader}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={loadingTeamLeaders}
                    >
                      {loadingTeamLeaders ? (
                        <option value="">Loading team leaders...</option>
                      ) : (
                        <>
                          <option value="">No Team Leader</option>
                          {teamLeaders.map((leader) => (
                            <option key={leader.agent_id} value={leader.agent_id}>
                              {leader.agent_id} - {leader.name} ({leader.role})
                            </option>
                          ))}
                        </>
                      )}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Select a team leader for this agent, or choose "No Team Leader"
                    </p>
                  </div>
                ) : (
                  // Non-admin users see fixed read-only display with their own ID
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                    {currentAgent?.agent_id} - {currentAgent?.name}
                    <input
                      type="hidden"
                      name="teamLeader"
                      value={formData.teamLeader}
                    />
                  </div>
                )}
              </div>

              {/* Category Field - Only for admin users */}
              {isAdmin && (
                <div>
                  <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                    Agent Category
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="POP">POP</option>
                    <option value="PLSSS">PLSSS</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Select the category for this agent (default: POP)
                  </p>
                </div>
              )}

              {/* Category Display for Non-Admin Users */}
              {!isAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Agent Category
                  </label>
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                    {formData.category}
                    <input
                      type="hidden"
                      name="category"
                      value={formData.category}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Category matches your account category
                  </p>
                </div>
              )}

              {/* Configuration Summary */}
              <div className="bg-gray-50 p-4 rounded-md">
                <h3 className="text-sm font-medium text-gray-700 mb-2">Agent Configuration</h3>
                <div className="space-y-1 text-sm text-gray-600">
                  <p><strong>Created By:</strong> {currentAgent?.agent_id} - {currentAgent?.name}</p>
                  <p><strong>Team Leader:</strong> {formData.teamLeader || 'None'}</p>
                  <p><strong>Role:</strong> {roleOptions.find(opt => opt.value === formData.role)?.label || formData.role}</p>
                  <p><strong>Category:</strong> {formData.category}</p>
                  <p><strong>Status:</strong> Active</p>
                  <p><strong>Document ID:</strong> {formData.agentId && formData.phoneNo ? `${formData.agentId}${formData.phoneNo.replace('+', '')}` : 'AgentID + PhoneNo (without +)'}</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={loading}
                  className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || agentIdCheck.available === false}
                  className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center">
                      <svg className="w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Creating...
                    </span>
                  ) : (
                    'Create Agent'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateAgentModal;
